import { useState, useEffect, useCallback } from 'react';
import { useServices, useToast } from '@/app/providers';
import type { AnnualBillingReport, AnnualBillingParams, ExportFormat, ChargeType } from '@yawi/types';
import { downloadBlob, generateReportFilename } from '../utils/download';

export const useAnnualBilling = () => {
  const { reportRepo } = useServices();
  const toast = useToast();

  const currentYear = new Date().getFullYear();

  const [report, setReport] = useState<AnnualBillingReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [params, setParams] = useState<AnnualBillingParams>({
    year: currentYear,
  });

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await reportRepo.getAnnualBillingReport(params);
      setReport(data);
    } catch (e) {
      console.error('Error loading annual billing report:', e);
      toast.error('Error al cargar la tabla de cobranza');
    } finally {
      setLoading(false);
    }
  }, [reportRepo, params, toast]);

  const exportReport = useCallback(async (format: ExportFormat) => {
    setExporting(true);
    try {
      const blob = await reportRepo.exportAnnualBillingReport(format, params);
      const filename = generateReportFilename(`cobranza-${params.year}`, format);
      downloadBlob(blob, filename);
      toast.success('Reporte exportado correctamente');
    } catch (e) {
      console.error('Error exporting annual billing report:', e);
      toast.error('Error al exportar el reporte');
    } finally {
      setExporting(false);
    }
  }, [reportRepo, params, toast]);

  const setYear = useCallback((year: number) => {
    setParams(prev => ({ ...prev, year }));
  }, []);

  const setChargeType = useCallback((chargeType: ChargeType | undefined) => {
    setParams(prev => ({ ...prev, chargeType }));
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    report,
    loading,
    exporting,
    params,
    setParams,
    setYear,
    setChargeType,
    refetch: fetchReport,
    exportReport,
    currentYear,
  };
};
